import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Download, Chrome, CheckCircle, ArrowRight, Tag } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExtensionDownload() {
  const handleDownload = () => {
    window.location.href = `${API_URL}/api/download/extension`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50 to-cyan-50 pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center">
              <Tag className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            RabatHub Browser Extension
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Få automatisk de bedste rabatkoder når du shopper online. 
            Gratis, nemt og effektivt!
          </p>
        </div>

        {/* Download Card */}
        <Card className="mb-8 shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="flex items-center justify-center gap-2 text-2xl">
              <Chrome className="w-8 h-8 text-blue-500" />
              Download til Chrome
            </CardTitle>
            <CardDescription>
              Kompatibel med Chrome, Edge, Brave og andre Chromium-baserede browsere
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center pt-4">
            <Button 
              onClick={handleDownload}
              size="lg"
              className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-lg px-8 py-6"
              data-testid="download-extension-btn"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Extension (.zip)
            </Button>
            <p className="text-sm text-gray-500 mt-4">Version 1.0.0 • 12 KB</p>
          </CardContent>
        </Card>

        {/* Installation Steps */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Sådan installerer du extensionen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center font-bold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Download og pak ud</h3>
                  <p className="text-gray-600">Klik på download-knappen ovenfor og pak ZIP-filen ud på din computer.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center font-bold">
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Åbn Chrome Extensions</h3>
                  <p className="text-gray-600">
                    Gå til <code className="bg-gray-100 px-2 py-1 rounded">chrome://extensions</code> i din browser.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center font-bold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Aktivér udviklertilstand</h3>
                  <p className="text-gray-600">Slå "Udviklertilstand" til øverst i højre hjørne.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center font-bold">
                  4
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Indlæs extensionen</h3>
                  <p className="text-gray-600">Klik "Indlæs upakket" og vælg den mappe hvor du pakkede ZIP-filen ud.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Færdig!</h3>
                  <p className="text-gray-600">RabatHub ikonet vises nu i din browser. Klik på det for at se tilgængelige rabatkoder.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Hvad kan extensionen?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium">Auto-detect butikker</h4>
                  <p className="text-sm text-gray-600">Genkender automatisk hvilken butik du er på</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium">Vis rabatkoder</h4>
                  <p className="text-sm text-gray-600">Se alle tilgængelige rabatkoder med ét klik</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium">Auto-apply</h4>
                  <p className="text-sm text-gray-600">Anvender automatisk den bedste rabatkode ved checkout</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium">Kopier med ét klik</h4>
                  <p className="text-sm text-gray-600">Kopier rabatkoder direkte til udklipsholderen</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Back link */}
        <div className="text-center">
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <ArrowRight className="w-4 h-4 rotate-180" />
              Tilbage til RabatHub
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
